<template>
  <div class="semaforo-container">
    <div class="semaforo-inner">
      <div
        class="semaforo-circulo"
        :class="{ 'suspendido': estadoCaso == 3 }"
        :style="{ 
          backgroundColor: color,
          boxShadow: `0 0 5px ${color}, 0 0 8px rgba(0,0,0,0.3)`
        }"
        :title="textoEstado"
      ></div>
      <div 
        class="semaforo-texto-container"
        :class="{ 'critico': esCritico, 'vencido': esVencido, 'suspendido': estadoCaso == 3 }"
      >
        <div class="semaforo-estado" :class="{ 'suspendido': estadoCaso == 3 }" :style="{ color: color }">
          {{ estado }}
        </div>
        <div class="semaforo-tiempo">
          {{ tiempo }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import env from '../../env';

export default {
  props: {
    fechaInicio: { type: String, required: true },
    dias: { type: [String, Number], default: 0 },
    horas: { type: [String, Number], default: 0 },
    minutos: { type: [String, Number], default: 0 },
    idCaso: { type: [String, Number], default: null }, // ID del caso (opcional)
    estadoCaso: { type: [String, Number], default: null }, // Estado actual del caso
    tiempoRestanteBackend: { type: [Number, null], default: null },
    diasFestivos: { type: Array, default: () => [] },
    // El padre pasa el id del area del caso
    areaId: { type: [String, Number], default: null },
    tiempoAcumulado: { type: [Number, String], default: 0 },
    fechaSuspension: { type: [String, null], default: null },
  },

  // Esto es para el parpadeo del semaforo y que se actualice la hora cada minuto en tiempo real
  data() {
    return {
      timer: null,
      ahora: new Date(),
      casoCerrado: false, // Para evitar múltiples peticiones
      ultimoEstado: null, // Para detectar cambios de estado
      tiempoSuspension: 0, // Tiempo transcurrido durante la suspensión
      // fechaSuspension: null // ELIMINADO: Ya viene por props, causa conflicto
      fechaReferenciaBackend: new Date(), // Para optimización de cálculo
    };
  },
  watch: {
    tiempoRestanteBackend: {
      immediate: true,
      handler() {
        this.fechaReferenciaBackend = new Date();
      }
    },
    estadoCaso: {
      immediate: true,
      handler(nuevoEstado, antiguoEstado) {
        // Detectar cambio de estado activo a suspendido
        if ((antiguoEstado === 2 || antiguoEstado === '2') && 
            (nuevoEstado === 3 || nuevoEstado === '3')) {
          // Guardar fecha de suspensión
          // this.fechaSuspension = new Date(); // ELIMINADO: Se usa prop
          // console.log(`Caso #${this.idCaso} - Suspendido en: ${this.fechaSuspension}`);
        }
        
        // Detectar cambio de estado suspendido a activo (reactivación)
        if ((antiguoEstado === 3 || antiguoEstado === '3') && 
            (nuevoEstado === 2 || nuevoEstado === '2')) {
          // Resetear el tiempo de suspensión, el backend se encargará de ajustar el tiempo
          // console.log(`Caso #${this.idCaso} - Reactivado. Ajustando tiempos.`);
        }
        
        this.ultimoEstado = nuevoEstado;
      }
    }
  },
  mounted() {
    // Actualizar la hora actual cada 15 segundos
    this.timer = setInterval(() => {
      // Actualizar la hora actual
      this.ahora = new Date();
      // Verificar si ha cambiado el estado
      this.verificarCambioEstado();
    }, 15000); // 15000 ms = 15 segundos (mismo intervalo que Pendientes.vue)
  },
  beforeDestroy() {
    // Limpiar el temporizador cuando el componente se destruye
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    // Método para verificar si una fecha es festivo
    esFestivo(fecha) {
      try {
        const fechaSinHora = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
        return this.diasFestivos.some(festivo => 
          festivo.fecha.getFullYear() === fechaSinHora.getFullYear() &&
          festivo.fecha.getMonth() === fechaSinHora.getMonth() &&
          festivo.fecha.getDate() === fechaSinHora.getDate()
        );
      } catch (error) {
        console.error("Error al verificar festivo:", error);
        return false;
      }
    },
    
    // Método para verificar si una fecha/hora está dentro del horario laboral
    estaDentroHorarioLaboral(fecha) {
      // Si el caso está suspendido (estado 3), considerar que está fuera de horario laboral
      // para detener el cálculo de tiempo mientras esté suspendido
      if (this.estadoCaso === 3 || this.estadoCaso === '3') {
        // console.log(`Caso #${this.idCaso} - Suspendido: ignorando cálculo de horario laboral`);
        return false;
      }

      const hora = fecha.getHours();
      const minutos = fecha.getMinutes();
      const tiempoEnMinutos = hora * 60 + minutos;
      
      // Considerar sábado y domingo como fin de semana (no laborables)
      const esFinDeSemana = fecha.getDay() === 0 || fecha.getDay() === 6; // 0 = domingo, 6 = sábado
      const esFestivoHoy = this.esFestivo(fecha);

      // Aplicar la regla especial SOLO para el área/cargo de mantenimiento y logística
      // idCargo 15 o 16 e idArea 12 (el idCargo del usuario está en localStorage)
      const idCargoUsuario = parseInt(localStorage.getItem('idCargo'), 10);
      const esReglaMantenimiento = (parseInt(this.areaId, 10) === 12 && (idCargoUsuario === 15 || idCargoUsuario === 16));

      if (esReglaMantenimiento) {
        // Para mantenimiento/logística: no contar NINGÚN tiempo en sábados, domingos o festivos
        if (esFinDeSemana || esFestivoHoy) return false;
        // Días laborables: 06:00 a 22:00
        return tiempoEnMinutos >= 360 && tiempoEnMinutos <= 1320;
      }

      // Comportamiento por defecto para otras áreas/cargos: mantener la lógica previa
      if (esFinDeSemana || esFestivoHoy) {
        // Horario de fines no laborables (sábado, domingo y festivos): 07:00 a 20:30 (420 a 1230 minutos)
        return tiempoEnMinutos >= 420 && tiempoEnMinutos <= 1230;
      } else {
        // Horario de lunes a viernes: 06:00 a 22:00 (360 a 1320 minutos)
        return tiempoEnMinutos >= 360 && tiempoEnMinutos <= 1320;
      }
    },
    
    // Método para calcular la próxima hora laboral válida
    calcularProximaHoraLaboral(fecha) {
      let fechaAjustada = new Date(fecha);
      
      // Si ya está dentro de horario laboral, no se necesita ajuste
      if (this.estaDentroHorarioLaboral(fechaAjustada)) {
        return fechaAjustada;
      }

      // Determinar si aplicamos la regla especial de mantenimiento/logística
      const idCargoUsuario = parseInt(localStorage.getItem('idCargo'), 10);
      const esReglaMantenimiento = (parseInt(this.areaId, 10) === 24 && idCargoUsuario === 128);
      
      if (esReglaMantenimiento) {
        // Para mantenimiento/logística: saltar completamente sábados, domingos y festivos
        while (!this.estaDentroHorarioLaboral(fechaAjustada)) {
          fechaAjustada.setDate(fechaAjustada.getDate() + 1);
          fechaAjustada.setHours(6, 0, 0, 0);
        }
      } else {
        // Avanzar hora por hora hasta encontrar un horario válido (comportamiento por defecto)
        while (!this.estaDentroHorarioLaboral(fechaAjustada)) {
          // Si es después del horario laboral, avanzar al siguiente día a la hora de inicio
          const hora = fechaAjustada.getHours();
          const minutos = fechaAjustada.getMinutes();
          // Considerar sábado y domingo como fin de semana (no laborables)
          const esFinDeSemana = fechaAjustada.getDay() === 0 || fechaAjustada.getDay() === 6;
          const esFestivo = this.esFestivo(fechaAjustada);

          if ((esFinDeSemana || esFestivo) && (hora > 20 || (hora === 20 && minutos > 30))) {
            // Después del horario en fines no laborables (20:30), avanzar al siguiente día y buscar la próxima hora laboral
            fechaAjustada.setDate(fechaAjustada.getDate() + 1);
            // Iniciar en 06:00 por defecto; el bucle ajustará al inicio correcto según si el siguiente día es fin de semana o laborable
            fechaAjustada.setHours(6, 0, 0, 0);
          } else if (!esFinDeSemana && !esFestivo && hora >= 22) {
            // Después de las 22:00 en día normal, ir al siguiente día a las 06:00
            fechaAjustada.setDate(fechaAjustada.getDate() + 1);
            fechaAjustada.setHours(6, 0, 0, 0);
          } else if (hora < 6 || (hora === 6 && fechaAjustada.getMinutes() === 0)) {
            // Antes de las 06:00 en cualquier día, ajustar al mismo día a las 06:00
            fechaAjustada.setHours(6, 0, 0, 0);

            // Si es fin de semana o festivo, ajustar a las 07:00
            if (esFinDeSemana || esFestivo) {
              fechaAjustada.setHours(7, 0, 0, 0);
            }
          } else {
            // Caso límite - avanzar una hora para evitar bucle infinito
            fechaAjustada.setTime(fechaAjustada.getTime() + 60 * 60 * 1000);
          }
        }
      }
      
      return fechaAjustada;
    },
    
    // Método para calcular el tiempo efectivo entre dos fechas teniendo en cuenta horario laboral
    calcularTiempoEfectivo(fechaInicio, fechaFin) {
      // Si el caso está suspendido, no acumular más tiempo
      if (this.estadoCaso === 3 || this.estadoCaso === '3') {
        // console.log(`Caso #${this.idCaso} - Suspendido: retornando tiempo actual acumulado`);
        
        // Si es suspendido, devolver el tiempo acumulado hasta el momento de la suspensión
        // El backend debería proporcionar este valor en tiempoRestanteBackend
        if (this.tiempoRestanteBackend !== null) {
          const tiempoTranscurridoMs = this.tiempoMaximo - (this.tiempoRestanteBackend * 1000);
          // console.log(`Caso #${this.idCaso} - Tiempo acumulado hasta suspensión: ${tiempoTranscurridoMs/1000} segundos`);
          return tiempoTranscurridoMs;
        }
        return 0;
      }

      // Para casos recién reactivados o activos, calculamos en tiempo real usando el tiempo acumulado de suspensiones
      // NO usamos tiempoRestanteBackend aquí para permitir que el contador avance con 'ahora'

      // Ajustar la fecha de inicio si está fuera del horario laboral
      let fechaInicioAjustada = this.calcularProximaHoraLaboral(new Date(fechaInicio));
      
      // Si la fecha ajustada es posterior a la fecha fin, no hay tiempo efectivo
      if (fechaInicioAjustada > fechaFin) {
        return 0;
      }
      
      // Para cálculos que abarcan múltiples días, necesitamos una aproximación más precisa
      // Esta implementación simplificada proporciona un cálculo razonable para la mayoría de los casos
      
      // Si las fechas están en el mismo día y ambas dentro del horario laboral
      if (
        fechaInicioAjustada.getDate() === fechaFin.getDate() &&
        fechaInicioAjustada.getMonth() === fechaFin.getMonth() &&
        fechaInicioAjustada.getFullYear() === fechaFin.getFullYear() &&
        this.estaDentroHorarioLaboral(fechaInicioAjustada) &&
        this.estaDentroHorarioLaboral(fechaFin)
      ) {
        // Simplemente calcular la diferencia directa
        return fechaFin - fechaInicioAjustada;
      }
      
      // Para períodos más largos o que cruzan múltiples días
      // Usamos un enfoque simplificado que puede no ser 100% preciso para todos los casos
      // pero proporciona una aproximación razonable
      
      // Tiempo total transcurrido en milisegundos
      let tiempoTotal = 0;
      
      // Fecha temporal para iterar
      let fechaActual = new Date(fechaInicioAjustada);
      
      // Mientras no hayamos llegado a la fecha final
      while (fechaActual < fechaFin) {
        // Considerar sábado y domingo como fin de semana (no laborables)
        const esFinDeSemana = fechaActual.getDay() === 0 || fechaActual.getDay() === 6;
        const esFestivo = this.esFestivo(fechaActual);
        const horaActual = fechaActual.getHours();
        const minutosActual = fechaActual.getMinutes();

        // Definir el final del día laboral actual
        let finDiaLaboral;
        if (esFinDeSemana || esFestivo) {
          // 20:30 para fines no laborables (sábado, domingo y festivos)
          finDiaLaboral = new Date(fechaActual);
          finDiaLaboral.setHours(20, 30, 0, 0);
        } else {
          // 22:00 para días normales
          finDiaLaboral = new Date(fechaActual);
          finDiaLaboral.setHours(22, 0, 0, 0);
        }
        
        // Si la fecha final es anterior al fin del día laboral, usar esa
        if (fechaFin < finDiaLaboral) {
          finDiaLaboral = new Date(fechaFin);
        }
        
        // Si estamos dentro del horario laboral, contar el tiempo
        if (this.estaDentroHorarioLaboral(fechaActual)) {
          tiempoTotal += finDiaLaboral - fechaActual;
        }
        
        // Avanzar al siguiente día laboral
        fechaActual.setDate(fechaActual.getDate() + 1);
        
        // Reiniciar al inicio del día laboral
        if (esFinDeSemana || esFestivo) {
          fechaActual.setHours(7, 0, 0, 0);
        } else {
          fechaActual.setHours(6, 0, 0, 0);
        }

        // Si el siguiente día es fin de semana o festivo, ajustar la hora de inicio
        const siguienteDiaEsFinDeSemana = fechaActual.getDay() === 0 || fechaActual.getDay() === 6;
        const siguienteDiaEsFestivo = this.esFestivo(fechaActual);
        if (siguienteDiaEsFinDeSemana || siguienteDiaEsFestivo) {
          fechaActual.setHours(7, 0, 0, 0);
        }
      }
      
      
      // Restar el tiempo muerto acumulado
      // NOTA: Si estamos en el calculo de delta (fechaReferenciaBackend vs ahora), NO restamos tiempoAcumulado
      // porque eso ya venia descontado en el tiempoRestanteBackend.
      // Solo restamos si estamos en el cálculo total "lento" (fechaInicio vs ahora).
      // Detectamos si es cálculo total verificando si fechaInicio es igual a this.fechaInicio
      const esCalculoTotal = fechaInicio.getTime() === new Date(this.fechaInicio).getTime();
      
      if (this.tiempoAcumulado && esCalculoTotal) {
        tiempoTotal -= (Number(this.tiempoAcumulado) * 1000);
      }

      return tiempoTotal;
    },
    
    cerrarPorVencimiento() {
      if (this.casoCerrado || !this.idCaso) return;
      
      // ⚠️ DESHABILITADO - El backend ahora maneja automáticamente el vencimiento de casos
      // basándose en la fecha_vencimiento correctamente calculada.
      // El frontend solo debe MOSTRAR el estado, NO cambiarlo.
      // 
      // ANTES: Este método llamaba a /callcenter/cambiarEstado con cerrados=4
      // AHORA: Solo mostramos visualmente que el caso está vencido
      
      // console.log(`Caso #${this.idCaso} - Detectado vencimiento visual (backend maneja el estado real)`);
      
      // Solo emitir evento para actualizar la UI, sin cambiar el estado en BD
      // El backend ya debería haber marcado el caso como vencido según fecha_vencimiento
      return;
      
      /* CÓDIGO ORIGINAL COMENTADO:
      const estadoNumero = 4;
      axios.get(`${env.endpoint}callcenter/cambiarEstado`, {
        params: {
          idCaso: this.idCaso,
          cerrados: estadoNumero,
          observacion: "Caso vencido automáticamente por tiempo"
        }
      })
      .then((res) => {
        if (res.data.estado) {
          this.$emit('caso-vencido', {
            idCaso: this.idCaso,
            mensaje: 'Estado del caso cambiado a VENCIDO automáticamente'
          });
        } else {
          console.error(`Caso #${this.idCaso} - Error al cambiar estado:`, res.data);
        }
      })
      .catch((error) => {
        console.error(`Caso #${this.idCaso} - Error al cambiar estado por vencimiento:`, error);
        this.casoCerrado = false;
      });
      */
    },
    // Método para verificar si el estado del caso ha cambiado
    verificarCambioEstado() {
      // Si no hay ID de caso, no hay nada que verificar
      if (!this.idCaso) return;
      
      // Solo verificar casos activos o sin estado definido
      // NO verificar casos suspendidos (estado 3), anulados (estado 5) o vencidos (estado 4)
      const estadosAVerificar = [0, 2, '0', '2'];
      if (!estadosAVerificar.includes(this.estadoCaso)) {
        // console.log(`Caso #${this.idCaso} - Verificación saltada: estado ${this.estadoCaso} no requiere verificación`);
        return;
      }
      
      // Detectar si este caso fue reactivado recientemente (en los últimos 30 segundos)
      // usando tiempoRestanteBackend como indicador
      const casoRecienReactivado = this.ultimoEstado === 3 || this.ultimoEstado === '3';
      
      // Si el caso fue recién reactivado, dar tiempo al backend para actualizar correctamente
      if (casoRecienReactivado) {
        // console.log(`Caso #${this.idCaso} - Recién reactivado, esperando actualización del backend`);
        
        // Actualizar datos del backend para asegurar tiempo correcto
        axios.get(`${env.endpoint}callcenter/tiempoRestante`, {
          params: { idCaso: this.idCaso }
        })
        .then(resp => {
          // console.log(`Caso #${this.idCaso} - Tiempo restante actualizado tras reactivación: ${resp.data.tiempo_restante} segundos`);
          
          // Verificar si el caso debería vencerse
          if (resp.data.tiempo_restante <= 0) {
            // console.log(`Caso #${this.idCaso} - Tiempo agotado después de reactivación`);
            this.cerrarPorVencimiento();
          }
        })
        .catch(error => {
          console.error(`Caso #${this.idCaso} - Error al obtener tiempo restante:`, error);
        });
        
        return;
      }
      
      // Calcular si está vencido basado en tiempo transcurrido
      const fechaInicio = new Date(this.fechaInicio);
      const transcurridoMs = this.calcularTiempoEfectivo(fechaInicio, this.ahora);
      
      // Si el tiempo transcurrido supera el máximo, el caso está vencido
      if (transcurridoMs > this.tiempoMaximo && !this.casoCerrado) {
        // console.log(`Caso #${this.idCaso} - Detectado vencimiento durante actualización de timer`);
        this.cerrarPorVencimiento();
      }
    },
  },
  computed: {
    tiempoMaximo() {
      // Usar los tiempos específicos del requerimiento en milisegundos
      return (
        (parseInt(this.dias) || 0) * 24 * 60 * 60 * 1000 +
        (parseInt(this.horas) || 0) * 60 * 60 * 1000 +
        (parseInt(this.minutos) || 0) * 60 * 1000
      );
    },
    calculoTiempo() {
      // Validaciones y datos iniciales
      if (this.estadoCaso === 3 || this.estadoCaso === '3') {
        let tiempoTexto = 'PAUSADO';
        if (this.tiempoRestanteBackend !== null) {
             const tiempoRestanteMs = this.tiempoRestanteBackend * 1000;
             const diasRestantes = Math.floor(tiempoRestanteMs / (24 * 60 * 60 * 1000));
             const horasRestantes = Math.floor((tiempoRestanteMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
             const minutosRestantes = Math.floor((tiempoRestanteMs % (60 * 60 * 1000)) / (60 * 1000));
             
             let detalle = '';
             if (diasRestantes > 0) detalle += `${diasRestantes}d `;
             if (horasRestantes > 0) detalle += `${horasRestantes}h `;
             if (minutosRestantes > 0 || (diasRestantes === 0 && horasRestantes === 0)) detalle += `${minutosRestantes}m`;
             
             tiempoTexto = detalle || '0m';
        }
        return { 
            estado: 'SUSPENDIDO', 
            tiempo: tiempoTexto, 
            color: '#FFA726', 
            esVencido: false, 
            esCritico: false, 
            textoCorto: `SUSPENDIDO\n${tiempoTexto}`, 
            textoEstado: `SUSPENDIDO (${tiempoTexto})` 
        };
      }

      const idCargoUsuario = parseInt(localStorage.getItem('idCargo'), 10);
      const esReglaMantenimiento = (parseInt(this.areaId, 10) === 24 && idCargoUsuario === 128);

      // Si el backend proporciona tiempo y NO aplica la regla de mantenimiento, usar el valor del backend
      if (this.tiempoRestanteBackend !== null && !esReglaMantenimiento) {
        try {

          // OPTIMIZACIÓN DE RENDIMIENTO:
          // En lugar de calcular desde fechaInicio (que puede ser muy antigua y trabar el navegador),
          // usamos el tiempoRestanteBackend como base y le restamos solo lo transcurrido desde la última carga.
          
          let tiempoRestanteMs;
          
          // Si tenemos tiempo del backend, lo usamos como base
            // Calculamos cuánto tiempo LABORAL ha pasado desde que obtuvimos el dato del backend
            const deltaMs = this.calcularTiempoEfectivo(this.fechaReferenciaBackend, this.ahora);
            
            // El tiempo real es: (TiempoBackend * 1000) - TiempoTranscurridoDesdeCarga
            tiempoRestanteMs = (this.tiempoRestanteBackend * 1000) - deltaMs;
            
          let estado, tiempo, color, esVencido = false, esCritico = false, textoCorto;

          if (tiempoRestanteMs <= 0) {
            const tiempoVencidoMs = Math.abs(tiempoRestanteMs);
            const diasVencido = Math.floor(tiempoVencidoMs / (24 * 60 * 60 * 1000));
            const horasVencido = Math.floor((tiempoVencidoMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
            const minutosVencido = Math.floor((tiempoVencidoMs % (60 * 60 * 1000)) / (60 * 1000));
            let tiempoVencidoTexto = '';
            if (diasVencido > 0) tiempoVencidoTexto += `${diasVencido}d `;
            if (horasVencido > 0) tiempoVencidoTexto += `${horasVencido}h `;
            if (minutosVencido > 0 || (diasVencido === 0 && horasVencido === 0)) tiempoVencidoTexto += `${minutosVencido}m`;
            if (!tiempoVencidoTexto.trim()) tiempoVencidoTexto = '0m';
            estado = 'VENCIDO'; tiempo = tiempoVencidoTexto; textoCorto = `VENCIDO\n${tiempoVencidoTexto}`; color = '#ff3333'; esVencido = true;
          } else {
            const tiempoMaximoMs = this.tiempoMaximo;
            const diasRestantes = Math.floor(tiempoRestanteMs / (24 * 60 * 60 * 1000));
            const horasRestantes = Math.floor((tiempoRestanteMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
            const minutosRestantes = Math.floor((tiempoRestanteMs % (60 * 60 * 1000)) / (60 * 1000));
            
            // Calcular el total de minutos restantes para la lógica del semáforo
            const totalMinutosRestantes = (diasRestantes * 24 * 60) + (horasRestantes * 60) + minutosRestantes;
            
            // Calcular el tiempo total del ticket en minutos para usar proporciones
            const tiempoMaximoMinutos = tiempoMaximoMs / (60 * 1000);
            
            // Calcular umbrales proporcionales: 2/3 para amarillo y 1/3 para rojo
            const umbralAmarillo = tiempoMaximoMinutos * (2/3);
            const umbralRojo = tiempoMaximoMinutos * (1/3);

            // UMBRALES FIJOS (en minutos)
            const UMBRAL_AMARILLO = 10;
            const UMBRAL_ROJO = 5;
            
            let tiempoRestanteTexto = '';
            if (diasRestantes > 0) tiempoRestanteTexto += `${diasRestantes}d `;
            if (horasRestantes > 0) tiempoRestanteTexto += `${horasRestantes}h `;
            if (minutosRestantes > 0 || (diasRestantes === 0 && horasRestantes === 0)) tiempoRestanteTexto += `${minutosRestantes}m`;
            if (!tiempoRestanteTexto.trim()) tiempoRestanteTexto = '0m';
            
            // Lógica del semáforo proporcional: Verde (>2/3), Amarillo (1/3-2/3), Rojo (<1/3)
            if (totalMinutosRestantes > UMBRAL_AMARILLO) { 
              estado = 'CON TIEMPO'; 
              tiempo = tiempoRestanteTexto; 
              textoCorto = `CON TIEMPO\n${tiempoRestanteTexto}`; 
              color = '#4CAF50'; 
            }
            else if (totalMinutosRestantes > UMBRAL_ROJO) { 
              estado = 'PRÓX. VENCER'; 
              tiempo = tiempoRestanteTexto; 
              textoCorto = `PRÓX. VENCER\n${tiempoRestanteTexto}`; 
              color = '#FFA726'; 
            }
            else { 
              estado = 'CRÍTICO'; 
              tiempo = tiempoRestanteTexto; 
              textoCorto = `CRÍTICO\n${tiempoRestanteTexto}`; 
              color = '#ff3333'; 
              esCritico = true; 
            }
          }
          return { estado, tiempo, color, esVencido, esCritico, textoCorto, textoEstado: `${estado} (${tiempo})` };

        } catch (err) {
          console.error("Error calculation opt:", err);
          // Fallback a cálculo local (lento) si falla lo de arriba
        } 



      }

      // Por defecto (o cuando aplica la regla de mantenimiento), calcular localmente respetando horario laboral
      try {
        const fechaInicio = new Date(this.fechaInicio);
        const transcurridoMs = this.calcularTiempoEfectivo(fechaInicio, this.ahora);

        if (this.tiempoMaximo === 0) {
          return { estado: 'SIN TIEMPO', tiempo: 'DEFINIDO', color: 'green', esVencido: false, esCritico: false, textoCorto: 'SIN TIEMPO\nDEFINIDO' };
        }

        const tiempoRestanteMs = this.tiempoMaximo - transcurridoMs;
        let estado, tiempo, color, esVencido = false, esCritico = false, textoCorto;

        if (transcurridoMs > this.tiempoMaximo) {
          if (this.estadoCaso !== 4 && this.estadoCaso !== '4' && this.estadoCaso !== 3 && this.estadoCaso !== '3' && !this.casoCerrado) {
            this.cerrarPorVencimiento();
          }
          if (this.estadoCaso === 3 || this.estadoCaso === '3') {
            return { estado: 'SUSPENDIDO', tiempo: '', color: '#FFA726', esVencido: false, esCritico: false, textoCorto: 'SUSPENDIDO', textoEstado: 'SUSPENDIDO' };
          }
          const tiempoVencidoMs = transcurridoMs - this.tiempoMaximo;
          const diasVencido = Math.floor(tiempoVencidoMs / (24 * 60 * 60 * 1000));
          const horasVencido = Math.floor((tiempoVencidoMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
          const minutosVencido = Math.floor((tiempoVencidoMs % (60 * 60 * 1000)) / (60 * 1000));
          let tiempoVencidoTexto = '';
          if (diasVencido > 0) tiempoVencidoTexto += `${diasVencido}d `;
          if (horasVencido > 0) tiempoVencidoTexto += `${horasVencido}h `;
          if (minutosVencido > 0 || (diasVencido === 0 && horasVencido === 0)) tiempoVencidoTexto += `${minutosVencido}m`;
          if (!tiempoVencidoTexto.trim()) tiempoVencidoTexto = '0m';
          estado = 'VENCIDO'; tiempo = tiempoVencidoTexto; textoCorto = `VENCIDO\n${tiempoVencidoTexto}`; color = '#ff3333'; esVencido = true;
          if (this.idCaso && !this.casoCerrado) this.cerrarPorVencimiento();
        } else {
          const diasRestantes = Math.floor(tiempoRestanteMs / (24 * 60 * 60 * 1000));
          const horasRestantes = Math.floor((tiempoRestanteMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
          const minutosRestantes = Math.floor((tiempoRestanteMs % (60 * 60 * 1000)) / (60 * 1000));
          
          // Calcular el total de minutos restantes para la lógica del semáforo
          const totalMinutosRestantes = (diasRestantes * 24 * 60) + (horasRestantes * 60) + minutosRestantes;
          
          // Calcular el tiempo total del ticket en minutos para usar proporciones
          const tiempoMaximoMinutos = this.tiempoMaximo / (60 * 1000);
          
          // Calcular umbrales proporcionales: 2/3 para amarillo y 1/3 para rojo
          const umbralAmarillo = tiempoMaximoMinutos * (2/3);
          const umbralRojo = tiempoMaximoMinutos * (1/3);
          
          let tiempoRestanteTexto = '';
          if (diasRestantes > 0) tiempoRestanteTexto += `${diasRestantes}d `;
          if (horasRestantes > 0) tiempoRestanteTexto += `${horasRestantes}h `;
          if (minutosRestantes > 0 || (diasRestantes === 0 && horasRestantes === 0)) tiempoRestanteTexto += `${minutosRestantes}m`;
          if (!tiempoRestanteTexto.trim()) tiempoRestanteTexto = '0m';
          
          // Lógica del semáforo proporcional: Verde (>2/3), Amarillo (1/3-2/3), Rojo (<1/3)
          if (totalMinutosRestantes > umbralAmarillo) { 
            estado = 'CON TIEMPO'; 
            tiempo = tiempoRestanteTexto; 
            textoCorto = `CON TIEMPO\n${tiempoRestanteTexto}`; 
            color = '#4CAF50'; 
          }
          else if (totalMinutosRestantes > umbralRojo) { 
            estado = 'PRÓX. VENCER'; 
            tiempo = tiempoRestanteTexto; 
            textoCorto = `PRÓX. VENCER\n${tiempoRestanteTexto}`; 
            color = '#FFA726'; 
          }
          else { 
            estado = 'CRÍTICO'; 
            tiempo = tiempoRestanteTexto; 
            textoCorto = `CRÍTICO\n${tiempoRestanteTexto}`; 
            color = '#ff3333'; 
            esCritico = true; 
          }
        }

        return { estado, tiempo, color, esVencido, esCritico, textoCorto, textoEstado: `${estado} (${tiempo})` };
      } catch (error) {
        console.error('Error en cálculo de tiempo:', error);
        return { estado: 'ERROR', tiempo: 'CÁLCULO', color: 'gray', esVencido: false, esCritico: false, textoCorto: 'ERROR\nCALCULO', textoEstado: 'Error en el cálculo del tiempo' };
      }
    },
    color() {
      return this.calculoTiempo.color;
    },
    textoEstado() {
      return this.calculoTiempo.textoEstado;
    },
    estado() {
      return this.calculoTiempo.estado;
    },
    tiempo() {
      return this.calculoTiempo.tiempo;
    },
    esVencido() {
      return this.calculoTiempo.esVencido;
    },
    esCritico() {
      return this.calculoTiempo.esCritico;
    }
  }
};
</script>

<style scoped>
.semaforo-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-right: 5px;
}
.semaforo-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 5px;
  margin: 5px 0 5px 0;
}
.semaforo-circulo {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}
.semaforo-texto-container {
  background-color: rgba(245, 245, 245, 0.85);
  border-radius: 4px;
  padding: 3px 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  width: 80px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  margin: 3px 0 3px 0;
}
.semaforo-texto-container.suspendido {
  background-color: rgba(255, 248, 225, 0.9);
  border-color: rgba(255, 167, 38, 0.5);
}
.semaforo-texto-container.critico,
.semaforo-texto-container.vencido {
  background-color: rgba(255, 245, 245, 0.9);
  border-color: rgba(255, 100, 100, 0.3);
}
.semaforo-estado {
  font-size: 10px;
  font-weight: bold;
  margin-bottom: 1px;
  text-transform: uppercase;
  line-height: 1.1;
}
.semaforo-estado.suspendido {
  font-size: 9px;
  margin-bottom: 0;
  padding-bottom: 0;
}
.semaforo-tiempo {
  font-size: 9px;
  color: #555;
  line-height: 1.1;
}
/* @keyframes parpadeo {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
} */
/* .parpadeo {
  animation: parpadeo 1.2s infinite;
}
.critico .semaforo-estado,
.vencido .semaforo-estado {
  animation: parpadeo 1.2s infinite;
  :class="{ 'parpadeo': esCritico || esVencido }" // esto va debajo de la etiqueta de la semaforo-circulo dentro de style
} */
.semaforo-circulo.suspendido {
  border: 2px solid #FFA726;
  opacity: 0.8;
}
</style> 